import React from 'react';
import { Input, Checkbox, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

export const handleKpiCurrentFilter = (
  kpiCurrentFilter,
  initiatives,
  attributeFilters,
  setAttributeFilters,
  searchTerm,
  setSearchTerm,
  subsectors
) => {
  // Function to handle clearing all filters for the current KPI
  const handleClearFilters = () => {
    setAttributeFilters((prevFilters) => {
      // Create a new object without the current kpi's filter
      const newFilters = { ...prevFilters };
      delete newFilters[kpiCurrentFilter];
      return newFilters;
    });
    setSearchTerm(''); // Also clear the search term
  };
  if (kpiCurrentFilter === 'kpiSector') {
    const ownerNames = [
      ...new Set(
        initiatives.flatMap((i) =>
          (i.impact || [])
            .map((imp) => {
              const subsector = subsectors.find(
                (s) => s._id === imp?.subsector
              );
              return subsector ? subsector?.sector?.sectorName : '<not set>';
            })
            .filter(Boolean)
        )
      ),
    ];

    const allSelected =
      attributeFilters[kpiCurrentFilter] === undefined ||
      attributeFilters[kpiCurrentFilter]?.length === ownerNames.length;

    return (
      <div style={{ marginTop: '10px' }}>
        {attributeFilters[kpiCurrentFilter]?.length > 0 && (
          <Button
            icon={<CloseOutlined />}
            onClick={handleClearFilters}
            style={{ marginBottom: '10px' }}
          >
            Clear Filters
          </Button>
        )}

        <Input
          placeholder="Search sectors"
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          style={{ marginBottom: '10px' }}
        />

        <Checkbox
          indeterminate={
            attributeFilters[kpiCurrentFilter]?.length > 0 &&
            attributeFilters[kpiCurrentFilter]?.length < ownerNames.length
          }
          checked={allSelected}
          onChange={(e) =>
            updateAttributeFilters(e.target.checked ? ownerNames : [])
          }
        >
          (Select all)
        </Checkbox>

        {ownerNames
          .filter((item) =>
            item.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .map((item) => (
            <div key={item}>
              <Checkbox
                checked={
                  attributeFilters[kpiCurrentFilter] === undefined ||
                  attributeFilters[kpiCurrentFilter]?.includes(item)
                }
                onChange={(e) => {
                  const current = attributeFilters[kpiCurrentFilter] || [];
                  updateAttributeFilters(
                    e.target.checked
                      ? [...current, item]
                      : current.filter((v) => v !== item)
                  );
                }}
              >
                {item}
              </Checkbox>
            </div>
          ))}
      </div>
    );
  }
  if (kpiCurrentFilter === 'kpiSubsector') {
    const ownerNames = [
      ...new Set(
        initiatives.flatMap((i) =>
          (i.impact || [])
            .map((imp) => {
              const subsector = subsectors.find(
                (s) => s._id === imp?.subsector
              );
              return subsector ? subsector?.subsectorName : '<not set>';
            })
            .filter(Boolean)
        )
      ),
    ];

    const allSelected =
      attributeFilters[kpiCurrentFilter] === undefined ||
      attributeFilters[kpiCurrentFilter]?.length === ownerNames.length;

    return (
      <div style={{ marginTop: '10px' }}>
        {attributeFilters[kpiCurrentFilter]?.length > 0 && (
          <Button
            icon={<CloseOutlined />}
            onClick={handleClearFilters}
            style={{ marginBottom: '10px' }}
          >
            Clear Filters
          </Button>
        )}

        <Input
          placeholder="Search Subsectors"
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          style={{ marginBottom: '10px' }}
        />

        <Checkbox
          indeterminate={
            attributeFilters[kpiCurrentFilter]?.length > 0 &&
            attributeFilters[kpiCurrentFilter]?.length < ownerNames.length
          }
          checked={allSelected}
          onChange={(e) =>
            updateAttributeFilters(e.target.checked ? ownerNames : [])
          }
        >
          (Select all)
        </Checkbox>

        {ownerNames
          .filter((item) =>
            item.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .map((item) => (
            <div key={item}>
              <Checkbox
                checked={
                  attributeFilters[kpiCurrentFilter] === undefined ||
                  attributeFilters[kpiCurrentFilter]?.includes(item)
                }
                onChange={(e) => {
                  const current = attributeFilters[kpiCurrentFilter] || [];
                  updateAttributeFilters(
                    e.target.checked
                      ? [...current, item]
                      : current.filter((v) => v !== item)
                  );
                }}
              >
                {item}
              </Checkbox>
            </div>
          ))}
      </div>
    );
  }
  const allKpiValues = initiatives.flatMap((i) => i.KPI?.map((imp) => imp[kpiCurrentFilter]) || []);
  const uniqueValuesWithEmptyCheck = [...new Set(allKpiValues)];

  // Check if any initiative has no KPI
  const hasNoKpi = initiatives.some((i) => !i.KPI || i.KPI.length === 0);

  // Add "<not set>" as a unique value if any initiative has no KPI
  const uniqueValues = hasNoKpi ? [...uniqueValuesWithEmptyCheck, '<not set>'] : uniqueValuesWithEmptyCheck;

  const allSelected =
    attributeFilters[kpiCurrentFilter] === undefined ||
    attributeFilters[kpiCurrentFilter]?.length === uniqueValues.length;

  return (
    <div style={{ marginTop: '10px' }}>
      {/* Conditionally render the Clear Filters button if there are active filters */}
      {attributeFilters[kpiCurrentFilter]?.length > 0 && (
        <Button
          icon={<CloseOutlined />}
          onClick={handleClearFilters}
          style={{ marginBottom: '10px', color: 'blue' }}
        >
          Clear Filters
        </Button>
      )}

      <Input
        placeholder="Search"
        onChange={(e) => setSearchTerm(e.target.value)}
        value={searchTerm} // Controlled component for search input
        style={{ marginBottom: '10px' }}
      />

      <Checkbox
        indeterminate={
          attributeFilters[kpiCurrentFilter]?.length > 0 &&
          attributeFilters[kpiCurrentFilter]?.length < uniqueValues.length
        }
        checked={allSelected}
        onChange={(e) => {
          setAttributeFilters(e.target.checked ? { ...attributeFilters, [kpiCurrentFilter]: uniqueValues } : { ...attributeFilters, [kpiCurrentFilter]: [] });
        }}
      >
        (Select all)
      </Checkbox>

      {uniqueValues
        .filter((item) => String(item).toLowerCase().includes(searchTerm.toLowerCase()))
        .map((item) => (
          <div key={item}>
            <Checkbox
              checked={
                attributeFilters[kpiCurrentFilter] === undefined ||
                attributeFilters[kpiCurrentFilter]?.includes(item)
              }
              onChange={(e) => {
                const currentFilters = attributeFilters[kpiCurrentFilter] || [];
                setAttributeFilters(
                  e.target.checked
                    ? {
                      ...attributeFilters,
                      [kpiCurrentFilter]: [...currentFilters, item],
                    }
                    : {
                      ...attributeFilters,
                      [kpiCurrentFilter]: currentFilters.filter((val) => val !== item),
                    }
                );
              }}
            >
              {String(item)}
            </Checkbox>
          </div>
        ))}
    </div>
  );
};